/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import { Children, cloneElement, Component } from 'react';
import { Tooltip } from 'react-tooltip';
import PropTypes from 'prop-types';

import { LoadingSpinner, joinDataTestId } from '@motili/ui';
import Button from 'react-bootstrap/Button';

import styles from './MotiliTextButton.module.scss';

export class MotiliTextButtonGroup extends Component {
    static propTypes = {
        children: PropTypes.node,
        busy: PropTypes.bool,
        disabled: PropTypes.bool,
        style: PropTypes.object,
    };

    static defaultProps = {
        busy: false,
        disabled: false,
    };

    render() {
        return (
            <div
                className={styles['motili-text-button-group-container']}
                style={this.props.style}
            >
                <div
                    className={
                        styles[
                            'motili-text-button-group-busy-spinner-container'
                        ]
                    }
                >
                    <LoadingSpinner size='xsmall' busy={this.props.busy} />
                </div>
                {Children.map(this.props.children, (child, index) => (
                    <div
                        className={
                            styles['motili-text-button-group-button-container']
                        }
                    >
                        {(this.props.busy || this.props.disabled) && child
                            ? cloneElement(child, { disabled: true })
                            : child}
                    </div>
                ))}
            </div>
        );
    }
}

export default class MotiliTextButton extends Component {
    static propTypes = {
        onClick: PropTypes.func,
        text: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.string,
            PropTypes.object,
        ]).isRequired,
        disabled: PropTypes.bool,
        disabledTooltipMessage: PropTypes.string,
        busy: PropTypes.bool,
        acls: PropTypes.bool,
        fontSize: PropTypes.number,
        color: PropTypes.string,
        whiteSpace: PropTypes.string,
        dataTestId: PropTypes.string,
    };

    static defaultProps = {
        fontSize: 14,
        color: '#57c1e9',
        whiteSpace: '',
        acls: true,
    };

    render() {
        if (this.props.busy) {
            console.warn(
                'MotiliTextButton prop busy has been removed. Please wrap your MotiliTextButton in a MotiliTextButtonGroup to include a busy spinner. See src/js/common/components/buttons/MotiliTextButton.jsx for details.'
            );
        }
        return (
            <span
                style={{ display: 'flex' }}
                id='motili-text-button'
                data-test-id={
                    (this.props.dataTestId && joinDataTestId([this.props.dataTestId, 'button'])) ??
                    `${
                        typeof this.props.text === 'string'
                            ? this.props.text.replace(/\s+/g, '-')
                            : 'motili'
                    }-text-button`
                }
                onClick={e => {
                    if (
                        this.props.acls === false ||
                        this.props.disabled ||
                        !this.props.onClick
                    ) {
                        return undefined;
                    }
                    return this.props.onClick?.(e);
                }}
            >
                {this.props.acls === false || this.props.disabled ? (
                    <span
                        data-tooltip-content={this.props.disabledTooltipMessage}
                        data-tooltip-id='motili-text-button-tooltip'
                    >
                        <div
                            className={styles.motiliTextButtonDisabled}
                            style={{
                                fontSize: this.props.fontSize,
                                whiteSpace: this.props.whiteSpace,
                            }}
                        >
                            {this.props.text}
                        </div>
                        {this.props.disabledTooltipMessage && (
                            <Tooltip
                                id='motili-text-button-tooltip'
                                className={styles['text-button-tooltip']}
                                noArrow
                                offset={5}
                                opacity={100}
                                place='top-start'
                                disableStyleInjection='core'
                            />
                        )}
                    </span>
                ) : (
                    <div
                        className={styles.motiliTextButton}
                        style={{
                            fontSize: this.props.fontSize,
                            color: this.props.color,
                            whiteSpace: this.props.whiteSpace,
                        }}
                    >
                        {this.props.text}
                    </div>
                )}
            </span>
        );
    }
}

export function MotiliRealTextButton({
    text,
    disabled = false,
    onClick = () => {},
    type = 'button',
}) {
    return (
        <button
            className={styles['motili-real-button']}
            disabled={disabled}
            onClick={onClick}
            type={type}
        >
            {text}
        </button>
    );
}

MotiliRealTextButton.propTypes = {
    onClick: PropTypes.func,
    text: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    type: PropTypes.string,
};

export function MotiliCancelButton({
    onClick = () => {},
    text = 'CANCEL',
    disabled = false,
}) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            type='button'
            className={styles['motili-cancel-button']}
        >
            {text}
        </button>
    );
}

MotiliCancelButton.propTypes = {
    onClick: PropTypes.func,
    text: PropTypes.string,
};

export function MotiliCancelRealButton({
    onClick = () => {},
    text = 'CANCEL',
    disabled = false,
    moStyle = 'primary',
    block = false,
    size = 'md',
}) {
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            type='button'
            bsStyle='primary'
            className={classes[moStyle][block ? 'block' : size]}
        >
            {text}
        </Button>
    );
}

MotiliCancelRealButton.propTypes = {
    onClick: PropTypes.func,
    text: PropTypes.string,
    moStyle: PropTypes.oneOf([
        'normal',
        'primary',
        'navy',
        'action',
        'blueOutline',
        'navyOutline',
    ]),
    block: PropTypes.bool,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export function MotiliRealButton({
    text,
    disabled = false,
    onClick = () => {},
    type = 'button',
    moStyle = 'primary',
    block = false,
    size = 'md',
}) {
    return (
        <Button
            disabled={disabled}
            onClick={onClick}
            type={type}
            bsStyle='primary'
            className={classes[moStyle][block ? 'block' : size]}
        >
            {text}
        </Button>
    );
}

MotiliRealButton.propTypes = {
    onClick: PropTypes.func,
    text: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    moStyle: PropTypes.oneOf([
        'normal',
        'primary',
        'navy',
        'action',
        'blueOutline',
        'navyOutline',
    ]),
    block: PropTypes.bool,
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

const classes = {
    primary: {
        sm: 'motili-btn-primary-sm',
        md: 'motili-btn-primary',
        lg: 'motili-btn-primary-lg',
        block: 'motili-btn-primary-block',
    },
    normal: {
        sm: 'motili-btn-sm',
        md: 'motili-btn',
        lg: 'motili-btn-lg',
    },
    navy: {
        sm: 'motili-btn-navy-sm',
        md: 'motili-btn-navy',
        lg: 'motili-btn-navy-lg',
    },
    action: {
        sm: 'motili-btn-action-sm',
        md: 'motili-btn-action',
        lg: 'motili-btn-action-lg',
    },
    blueOutline: {
        sm: 'motili-btn-blue-outline-sm',
        md: 'motili-btn-blue-outline',
        lg: 'motili-btn-blue-outline-lg',
        block: 'motili-btn-blue-outline-block',
    },
    navyOutline: {
        sm: 'motili-btn-navy-outline-sm',
        md: 'motili-btn-navy-outline',
        lg: 'motili-btn-navy-outline-lg',
    },
};
